import Typography from '@mui/material/Typography';
import {
	DataGrid,
	GridToolbar,
	GridFooter,
	GridFooterContainer,
} from '@mui/x-data-grid';
import { React, useMemo } from 'react';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import FormHelperText from '@mui/material/FormHelperText';
import dayjs from 'dayjs';
import Stack from '@mui/material/Stack';
import CustomLoading from '../../shared/CustomLoading';
import { formatPhone } from '../../../utils/formatters';

const CenteredCell = ({ children }) => (
	<Stack justifyContent='center' sx={{ height: '100%', py: 2 }}>
		{children}
	</Stack>
);

CenteredCell.propTypes = {
	children: PropTypes.node,
};

const Footer = ({
	inverse = false,
	selectionModel = [],
	allChecked = false,
	totalRows = 0,
}) => {
	const renderText = () => {
		if (!selectionModel?.length && !allChecked) {
			return null;
		}
		if (inverse) {
			return `Location(s) to be removed: ${selectionModel.length}`;
		}
		if (allChecked) {
			return `Location(s) to be added: ${totalRows}`;
		}
		return `Location(s) to be added: ${selectionModel.length}`;
	};

	return (
		<GridFooterContainer>
			<div style={{ margin: '0 16px' }}>{renderText()}</div>
			<GridFooter />
		</GridFooterContainer>
	);
};

Footer.propTypes = {
	inverse: PropTypes.bool,
	selectionModel: PropTypes.array,
	allChecked: PropTypes.bool,
	totalRows: PropTypes.number,
};

const SubAccountLocationsTable = ({
	isLoading,
	pageTitle,
	locations,
	totalRows,
	error = undefined,
	inverse = false,
	locked = false,
	allChecked = false,
	selectionModel,
	selectionModelUpdate,
	page,
	onPageChange,
	pageSize,
	onPageSizeChange,
	onSearchStrChange,
}) => {
	// Inverse selection logic
	// If inverse, all rows are checked except those in selectionModel
	const allRowIds = useMemo(
		() => locations.map(row => row.drFedID),
		[locations]
	);

	const v8SelectionModel = useMemo(() => {
		if (locked && allChecked) {
			return { type: 'exclude', ids: new Set() };
		}
		if (inverse) {
			const displayedIds = allRowIds.filter(
				id => !selectionModel.includes(id)
			);
			return { type: 'include', ids: new Set(displayedIds) };
		}
		return { type: 'include', ids: new Set(selectionModel) };
	}, [allRowIds, locked, allChecked, inverse, selectionModel]);

	const paginationModel = useMemo(
		() => ({ page, pageSize }),
		[page, pageSize]
	);
	const handlePaginationChange = newModel => {
		if (newModel.page !== page) onPageChange(newModel.page);
		if (newModel.pageSize !== pageSize) onPageSizeChange(newModel.pageSize);
	};

	const columns = [
		{
			field: 'drFedID',
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Id
				</Typography>
			),
		},
		{
			field: 'clinicName', // don't change this field name, used in API
			flex: 1,
			minWidth: 300,
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Locations
				</Typography>
			),
			renderCell: params => (
				<CenteredCell>{params.formattedValue}</CenteredCell>
			),
		},
		{
			field: 'address',
			flex: 1,
			minWidth: 300,
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Address
				</Typography>
			),
			renderCell: params => (
				<CenteredCell>
					<div> {params.row.address1}</div>
					<div> {params.row.address2}</div>
					<div>
						{params.row.city}, {params.row.state}{' '}
						{params.row.zipCode}
					</div>
				</CenteredCell>
			),
		},
		{
			field: 'info',
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Info
				</Typography>
			),
			width: 200,
			renderCell: params => (
				<CenteredCell>
					<div>
						<strong>Phone: </strong>
						{formatPhone(params.row.phone) || 'N/A'}
					</div>
					<div>
						<strong>Fax: </strong>
						{formatPhone(params.row.fax) || 'N/A'}
					</div>
					<div>
						<strong>Tax ID #: </strong>
						{params.row.taxID}
					</div>
				</CenteredCell>
			),
		},
		{
			field: 'drEffectiveDate',
			width: 120,
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Effective
				</Typography>
			),
			valueFormatter: value => {
				if (!value) return '';
				return dayjs(value).format('MM/DD/YYYY');
			},
			renderCell: params => (
				<CenteredCell>{params.formattedValue}</CenteredCell>
			),
		},
		{
			field: 'drTerminationDate',
			width: 120,
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Termed
				</Typography>
			),
			valueFormatter: value => {
				if (!value) return '';
				return dayjs(value).format('MM/DD/YYYY');
			},
			renderCell: params => (
				<CenteredCell>{params.formattedValue}</CenteredCell>
			),
		},
	];

	return (
		<Box
			sx={{
				mt: 6,
			}}
		>
			<Typography variant='h6' component='label' sx={{ mb: 1 }}>
				{pageTitle}
			</Typography>
			<DataGrid
				rows={locations}
				rowCount={totalRows}
				getRowId={row => row.drFedID}
				showToolbar
				autoHeight
				pagination
				disableColumnMenu
				disableColumnFilter
				disableColumnResize
				disableColumnSelector
				disableDensitySelector
				checkboxSelection
				isRowSelectable={() => !locked}
				keepNonExistentRowsSelected
				hideFooterSelectedRowCount
				getRowHeight={() => 'auto'}
				getEstimatedRowHeight={() => 100}
				pageSizeOptions={[10, 20, 50]}
				columns={columns}
				rowSelectionModel={v8SelectionModel}
				paginationMode='server'
				paginationModel={paginationModel}
				onPaginationModelChange={handlePaginationChange}
				loading={isLoading}
				onRowSelectionModelChange={newModel => {
					if (locked && allChecked) {
						// Ignore selection changes when locked/allChecked
						return;
					}
					const selectedIds = Array.from(newModel.ids);
					if (inverse) {
						// Inverse: unchecked = selected (to be removed)
						const unchecked = allRowIds.filter(
							id => !selectedIds.includes(id)
						);
						selectionModelUpdate(unchecked);
					} else {
						selectionModelUpdate(selectedIds);
					}
				}}
				slots={{
					toolbar: GridToolbar,
					loadingOverlay: CustomLoading,
					footer: Footer,
				}}
				slotProps={{
					toolbar: {
						showQuickFilter: true,
						printOptions: {
							disableToolbarButton: true,
						},
						csvOptions: { disableToolbarButton: true },
						quickFilterProps: { debounceMs: 300 },
					},
					footer: {
						inverse,
						selectionModel,
						allChecked,
						totalRows,
						pageTitle,
					},
					loadingOverlay: {
						variant: 'skeleton',
						noRowsVariant: 'skeleton',
						'data-testid': 'datagridLoadingOverlay',
					},
				}}
				sx={{
					'.MuiDataGrid-columnHeaderTitleContainer': {
						'.MuiSvgIcon-root': {
							color: 'white',
						},
					},
				}}
				columnVisibilityModel={{
					drFedID: false,
				}}
				filterMode='server'
				onFilterModelChange={newFilterModel => {
					onSearchStrChange(
						newFilterModel.quickFilterValues.join(' ')
					);
				}}
			/>
			{error && <FormHelperText error>{error}</FormHelperText>}
		</Box>
	);
};

SubAccountLocationsTable.propTypes = {
	isLoading: PropTypes.bool.isRequired,
	locked: PropTypes.bool,
	allChecked: PropTypes.bool,
	inverse: PropTypes.bool,
	pageTitle: PropTypes.string.isRequired,
	locations: PropTypes.arrayOf(PropTypes.object).isRequired,
	error: PropTypes.string,
	selectionModel: PropTypes.arrayOf(PropTypes.object).isRequired,
	selectionModelUpdate: PropTypes.func.isRequired,
	page: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	onPageSizeChange: PropTypes.func.isRequired,
	totalRows: PropTypes.number.isRequired,
	onSearchStrChange: PropTypes.func.isRequired,
};

export default SubAccountLocationsTable;
